import type { ReactNode } from "react";
import { ExternalLink, Linkedin } from "lucide-react";
import type { NavItem } from "@appcaire/shared/public-navigation";
import { CaireLovingHandsMark } from "../brand/CaireLovingHandsMark";
import { CaireWordmark } from "../brand/CaireWordmark";

const EIRTECH_URL = "https://www.eirtech.ai/";
const footerLinkClass = "text-[#d7dde6] transition-colors hover:text-white";
const eirtechFooterLinkClass =
  "inline-flex items-center gap-1 font-semibold text-caire-accent-soft underline decoration-current underline-offset-4 transition-colors hover:text-white";

export type PublicSiteFooterSection = {
  id: string;
  label: string;
  items: NavItem[];
};

export type PublicSiteFooterProps = {
  locale: "sv" | "en";
  description: string;
  rights: string;
  sections: PublicSiteFooterSection[];
  contact: { label: string; href: string };
  legalLinks: Array<{ label: string; href: string }>;
  /** Names the legal links as a landmark where the host wants one. */
  legalNavigationLabel?: string;
  /** The app that owns the cookie choice adds its control here. */
  legalExtra?: ReactNode;
  /** The host owns navigation: a router link on the website, a site URL elsewhere. */
  renderLink: (args: {
    href: string;
    className: string;
    children: ReactNode;
  }) => ReactNode;
};

const contactClass =
  "rounded-lg bg-caire-action px-6 py-2 font-medium text-caire-on-solid transition-colors hover:bg-caire-action-hover";
const legalLinkClass = "text-[#b5bfca] transition-colors hover:text-white";

/**
 * The public site footer: brand and contact row, the five navigation hubs,
 * legal and copyright. One component for the website and every public app page.
 */
export function PublicSiteFooter({
  locale,
  description,
  rights,
  sections,
  contact,
  legalLinks,
  legalNavigationLabel,
  legalExtra,
  renderLink,
}: PublicSiteFooterProps) {
  return (
    <footer className="border-t border-[#1b3545] bg-[#0d2535] text-sm leading-relaxed text-[#d7dde6]">
      <div className="mx-auto w-full max-w-[1440px] px-6 py-10 lg:px-10">
        <div className="space-y-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="max-w-xl">
              <div className="mb-3 flex items-center">
                <span className="mr-3 flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--cb-action)] text-white">
                  <CaireLovingHandsMark className="h-5 w-5" />
                </span>
                <CaireWordmark
                  className="text-2xl text-white"
                  variant="inverse"
                  showDescriptor
                  locale={locale}
                />
              </div>
              <p className="max-w-md text-base leading-relaxed text-[#d7dde6]">
                {description}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-4">
              <a
                href="https://www.linkedin.com/company/caire-homecare"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="text-white transition-colors hover:text-[var(--cb-accent)]"
              >
                <Linkedin className="h-6 w-6" />
              </a>
              {renderLink({
                href: contact.href,
                className: contactClass,
                children: contact.label,
              })}
            </div>
          </div>
          {/* Curated hub lists sourced from the shared footer taxonomy, which
              mirrors the header so crawlers read one structure. */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-7 md:grid-cols-3 lg:grid-cols-5">
            {sections.map((section) => (
              <nav key={section.id} aria-labelledby={`footer-${section.id}`}>
                <h3
                  id={`footer-${section.id}`}
                  className="mb-3 text-sm font-semibold text-white"
                >
                  {section.label}
                </h3>
                <ul className="space-y-2">
                  {section.items.map((item) => (
                    <li key={item.href}>
                      {item.target === "_blank" ? (
                        <a
                          href={item.href}
                          target="_blank"
                          rel={item.rel ?? "noreferrer"}
                          className={footerLinkClass}
                        >
                          {item.label}
                        </a>
                      ) : (
                        renderLink({
                          href: item.href,
                          className: footerLinkClass,
                          children: item.label,
                        })
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center justify-between gap-4 px-6 py-5 md:flex-row lg:px-10">
          <p className="text-sm text-[#b5bfca]">
            © {new Date().getFullYear()}{" "}
            <a
              href={EIRTECH_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={eirtechFooterLinkClass}
              aria-label="EirTech AB"
            >
              EirTech AB
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
            . {rights}
            <span className="ml-2 whitespace-nowrap">Org-nr 559522-3800</span>
          </p>
          <nav
            aria-label={legalNavigationLabel}
            className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm"
          >
            {legalLinks.map((item) => (
              <span key={item.href}>
                {renderLink({
                  href: item.href,
                  className: legalLinkClass,
                  children: item.label,
                })}
              </span>
            ))}
            {legalExtra}
          </nav>
        </div>
      </div>
    </footer>
  );
}
